import React, { useCallback, useEffect, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { logBreadcrumb } from '../../services/errorHandler'
import { isAndroid, isMobileDevice } from '../../utils/device'
import { toSafeExternalUrl } from '../../utils/url/safe-external-url'
import { ActionCard } from './design/ActionCard'
import { AppDownloadCard } from './design/AppDownloadCard'
import { AuthLayout } from './design/AuthLayout'
import { LockIcon } from './design/icons'
import { COLORS, FONT } from './design/tokens'

const OPEN_IN_APP_TIMEOUT_MS = 2_500
// Safety reset for the per-card pending state. If the redirect/handoff
// succeeds the SPA unmounts and this never fires; if it doesn't (slow
// network, bfcache restore, env misconfig leaving the URL empty) the card
// either becomes tappable again (reusable link) or transitions to a clear
// "Already used" state (one-time magic-token link).
const PENDING_RESET_TIMEOUT_MS = 8_000

type CardState = 'idle' | 'pending' | 'consumed'

interface Props {
    /**
     * Pre-built sama-coachee:// deep-link URL for "Open in Sama app". The caller
     * (LoginCoacheePage) constructs this via `buildSamaCoacheeDeepLink` so the
     * chooser stays a pure presentation component. The scheme is sourced from
     * a hardcoded constant and path segments are encoded via
     * `encodeURIComponent`; no user-controlled input reaches the scheme.
     */
    deepLinkUrl: string
    /** Fires when the user picks "Continue in browser". */
    onContinueOnWeb: () => void
    appStoreUrl: string
    playStoreUrl: string
    /**
     * True when the chooser was reached with a `magicToken` query param. Both
     * actions then forward to a one-time-use endpoint, so after activation the
     * card transitions to a permanent "consumed" state instead of resetting.
     */
    isOneTimeMagicTokenLink: boolean
}

export const MagicLinkChooser: React.FC<Props> = ({
    deepLinkUrl,
    onContinueOnWeb,
    appStoreUrl,
    playStoreUrl,
    isOneTimeMagicTokenLink,
}) => {
    const { t } = useTranslation()
    const [openInAppState, setOpenInAppState] = useState<CardState>('idle')
    const [continueOnWebState, setContinueOnWebState] =
        useState<CardState>('idle')
    const [statusMessage, setStatusMessage] = useState('')
    const fallbackTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null)
    const visibilityListenerRef = useRef<(() => void) | null>(null)
    const openInAppResetTimerRef = useRef<ReturnType<typeof setTimeout> | null>(
        null,
    )
    const continueOnWebResetTimerRef = useRef<
        ReturnType<typeof setTimeout> | null
    >(null)

    // Protocol allow-list rather than DOMPurify — sanitising a plain string
    // doesn't validate scheme. If the env-derived URL isn't a valid http(s)
    // URL we drop the fallback redirect entirely; the user stays on the
    // chooser and can pick a different action.
    const storeUrlForCurrentPlatform = isAndroid() ? playStoreUrl : appStoreUrl
    const safeStoreUrl = toSafeExternalUrl(storeUrlForCurrentPlatform)

    // Mobile frames a choice ("How to continue?" + 2 actions); desktop has only
    // the browser path, so it uses the sign-in heading + a single action. Cards
    // are intentionally minimal — one line, no icon or description — and the
    // filled primary card carries the "recommended" weight on its own (top +
    // viridian fill), so no separate badge is needed.
    const mobile = isMobileDevice()
    const pageTitle = mobile
        ? t('MagicLinkChooser.title')
        : t('titles.logIn')
    const continueCardTitle = mobile
        ? t('MagicLinkChooser.continueOnWeb')
        : t('MagicLinkChooser.continueToSama')
    // On desktop the continue card is the only action, so it carries the primary
    // visual weight (viridian fill) instead of the outlined secondary look used
    // on mobile (where it sits below the recommended app card).
    const continueCardIsPrimary = !mobile

    const cleanupFallback = useCallback(() => {
        if (fallbackTimerRef.current) {
            clearTimeout(fallbackTimerRef.current)
            fallbackTimerRef.current = null
        }
        if (visibilityListenerRef.current) {
            document.removeEventListener(
                'visibilitychange',
                visibilityListenerRef.current,
            )
            visibilityListenerRef.current = null
        }
    }, [])

    const cleanupResetTimers = useCallback(() => {
        if (openInAppResetTimerRef.current) {
            clearTimeout(openInAppResetTimerRef.current)
            openInAppResetTimerRef.current = null
        }
        if (continueOnWebResetTimerRef.current) {
            clearTimeout(continueOnWebResetTimerRef.current)
            continueOnWebResetTimerRef.current = null
        }
    }, [])

    // One-time magic-token landings: both actions forward to the same
    // single-use endpoint, so tapping either must lock the *whole* chooser,
    // not just the tapped card. Otherwise a user could tap "Open in app",
    // come back via back / bfcache, and burn the second redirect on a token
    // that's already been consumed.
    const setAllCardStates = useCallback<
        React.Dispatch<React.SetStateAction<CardState>>
    >((next) => {
        setOpenInAppState(next)
        setContinueOnWebState(next)
    }, [])

    useEffect(() => {
        logBreadcrumb('magic_link_chooser_shown')
        return () => {
            cleanupFallback()
            cleanupResetTimers()
        }
    }, [cleanupFallback, cleanupResetTimers])

    const scheduleReset = useCallback(
        (
            timerRef: React.MutableRefObject<
                ReturnType<typeof setTimeout> | null
            >,
            setState: React.Dispatch<React.SetStateAction<CardState>>,
        ) => {
            timerRef.current = setTimeout(() => {
                timerRef.current = null
                if (isOneTimeMagicTokenLink) {
                    logBreadcrumb('magic_link_consumed_shown')
                    setStatusMessage(
                        t('MagicLinkChooser.linkAlreadyUsedStatus'),
                    )
                    setState('consumed')
                } else {
                    logBreadcrumb('magic_link_pending_reset')
                    setState('idle')
                }
            }, PENDING_RESET_TIMEOUT_MS)
        },
        [isOneTimeMagicTokenLink, t],
    )

    const handleOpenInApp = useCallback(() => {
        if (openInAppState !== 'idle') return
        if (isOneTimeMagicTokenLink && continueOnWebState !== 'idle') return

        // Tear down any leftover visibilitychange listener / fallback timer
        // from a previous attempt before registering new ones. The 8s safety
        // reset returns the card to idle on the reusable-link path, so a
        // re-tap could otherwise stack orphan listeners on `document`.
        cleanupFallback()

        if (isOneTimeMagicTokenLink) {
            setAllCardStates('pending')
        } else {
            setOpenInAppState('pending')
        }
        logBreadcrumb('open_in_app_clicked')
        setStatusMessage(t('MagicLinkChooser.openingApp'))

        window.location.href = deepLinkUrl

        const onVisibilityChange = () => {
            if (document.visibilityState === 'hidden') {
                cleanupFallback()
            }
        }
        document.addEventListener('visibilitychange', onVisibilityChange)
        visibilityListenerRef.current = onVisibilityChange

        fallbackTimerRef.current = setTimeout(() => {
            // Once the fallback fires we're force-redirecting to the store
            // (or no-op if safeStoreUrl is null), so the visibility listener
            // is moot — clear it before any user-visible side effects.
            cleanupFallback()
            logBreadcrumb('app_store_fallback_fired')
            setStatusMessage(t('MagicLinkChooser.appNotDetected'))
            // safeStoreUrl is null when the env URL fails the protocol check.
            // Skip the navigation rather than redirect to a no-op string —
            // user stays on the chooser with the status message visible.
            if (safeStoreUrl) {
                window.location.href = safeStoreUrl
            }
        }, OPEN_IN_APP_TIMEOUT_MS)

        scheduleReset(
            openInAppResetTimerRef,
            isOneTimeMagicTokenLink ? setAllCardStates : setOpenInAppState,
        )
    }, [
        cleanupFallback,
        continueOnWebState,
        deepLinkUrl,
        isOneTimeMagicTokenLink,
        openInAppState,
        safeStoreUrl,
        scheduleReset,
        setAllCardStates,
        t,
    ])

    const handleContinueOnWeb = useCallback(() => {
        if (continueOnWebState !== 'idle') return
        if (isOneTimeMagicTokenLink && openInAppState !== 'idle') return

        // Clear any pending open-in-app store fallback before handing off to
        // the web flow. In reusable-link mode the open-in-app guard above
        // doesn't block this action, so a leftover fallback timer from a prior
        // "Open in app" tap could otherwise fire and bounce the user to the
        // app store mid-handoff.
        cleanupFallback()

        if (isOneTimeMagicTokenLink) {
            setAllCardStates('pending')
        } else {
            setContinueOnWebState('pending')
        }
        logBreadcrumb('continue_on_web_clicked')
        onContinueOnWeb()
        scheduleReset(
            continueOnWebResetTimerRef,
            isOneTimeMagicTokenLink ? setAllCardStates : setContinueOnWebState,
        )
    }, [
        cleanupFallback,
        continueOnWebState,
        isOneTimeMagicTokenLink,
        onContinueOnWeb,
        openInAppState,
        scheduleReset,
        setAllCardStates,
    ])

    const handleStoreBadgeClick = useCallback(() => {
        logBreadcrumb('download_app_clicked')
    }, [])

    const alreadyUsedBadge = t('MagicLinkChooser.alreadyUsedBadge')

    return (
        <AuthLayout
            mobile={mobile}
            bottomSlot={
                <AppDownloadCard
                    appStoreUrl={appStoreUrl}
                    playStoreUrl={playStoreUrl}
                    onBadgeClick={handleStoreBadgeClick}
                />
            }
            footer={
                <div
                    style={{
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        gap: 6,
                        marginTop: 16,
                        color: COLORS.neutral500,
                        fontFamily: FONT.display,
                        fontSize: 12,
                        letterSpacing: '0.005em',
                    }}
                >
                    <LockIcon size={13} />
                    <span>{t('MagicLinkChooser.secureNote')}</span>
                </div>
            }
            statusNode={
                <div role="status" aria-live="polite" className="sr-only">
                    {statusMessage}
                </div>
            }
        >
            <h1
                style={{
                    fontSize: 28,
                    color: COLORS.noir,
                    letterSpacing: '-0.02em',
                    lineHeight: 1.15,
                    margin: 0,
                    textAlign: 'center',
                }}
            >
                {pageTitle}
            </h1>

            <div
                style={{
                    marginTop: 28,
                    display: 'flex',
                    flexDirection: 'column',
                    gap: 14,
                }}
            >
                {mobile && (
                    <ActionCard
                        primary
                        ribbon={t('MagicLinkChooser.recommended')}
                        title={t('MagicLinkChooser.openInApp')}
                        pending={openInAppState === 'pending'}
                        consumed={openInAppState === 'consumed'}
                        consumedLabel={alreadyUsedBadge}
                        onClick={handleOpenInApp}
                    />
                )}
                <ActionCard
                    primary={continueCardIsPrimary}
                    title={continueCardTitle}
                    pending={continueOnWebState === 'pending'}
                    consumed={continueOnWebState === 'consumed'}
                    consumedLabel={alreadyUsedBadge}
                    onClick={handleContinueOnWeb}
                />
            </div>
        </AuthLayout>
    )
}

export default MagicLinkChooser
